<!-- eslint-disable -->

<template>
        <Button
          class="m-2"
          @click="copyQuote"
          v-if="quote?.quote_line_items?.length && !loading"
        >Copy Quote Line Items</Button>
</template>

<script setup lang="ts">
/* eslint-disable */

import { toRefs, toRaw, isProxy, PropType, isRef, Ref } from "vue";
import { useRouter } from "vue-router";
import {
  useInsertQuoteMutation,
  useGetQuotesCountQuery,
  AllQuoteFieldsFragment as QuoteType,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
  Quote_Source_Dataset_Enum,
  Quote_Status_Dataset_Enum,
  Quote_Line_Item_Source_Dataset_Enum,
} from "gql/api";
import {
  exclude_fields,
  fixDate,
  isServiceLineItemAddedFromJobType,
  getArrayFromDeepNestedProxy
} from "@/utils/quoteHelpers";

import { captureException } from "@sentry/vue";

const quoteHeaderFields = [
  "prepared_by_id",
  "sk_job_type",
  "comments",
  "currency",
  "customer_rep_name",
  "customer_rep_email",
  "customer_rep_phone_number",
  "directions",
  "erp_contract_number",
  "erp_customer_name",
  "erp_customer_number",
  "erp_nwa_id",
  "erp_nwa_name",
  "erp_plant_id",
  "erp_plant_name",
  "erp_price_list_name",
  "erp_price_list_number",
  "erp_well_country",
  "erp_well_id",
  "erp_well_name",
  "erp_well_uwi",
  "erp_well_region",
  "erp_address_id",
  "erp_address_name",
  "erp_full_address",
  "note",
  "pricing_date",
  "proposed_start_date",
  "sk_job_id",
  "sk_job_number",
  "sk_proposal_number",
  "error_log_json"
];

const quoteLineItemFields = [
  // "erp_price",
  // "price_override",
  // "margin",
  // "pricing_date"
] as (keyof QuoteLineItemType)[];

const excludeLineItemFields = [exclude_fields, quoteLineItemFields].flat()

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType | null>,
    default: null
  },
  loading: {
    type: Boolean,
    default: true
  }
});

const { quote, loading } = toRefs(props);
const router = useRouter();
const { executeMutation: insertQuote } = useInsertQuoteMutation();
const { executeQuery: getQuotesCount } = useGetQuotesCountQuery({
  requestPolicy: "network-only",
  pause: true,
  variables: {}
});
const serializeQuoteLineItem = (item: QuoteLineItemType) => {
  exclude_fields.forEach(field => {
    const fieldVal = item[field];
    delete item[field];
    if (fieldVal && fieldVal?.id) {
      (item as any)[`spyglass_${field}`] = fieldVal.id; // TODO: remove as any and resolve
    }
  });
  item.pricing_date = fixDate(new Date());
  return item;
};

const removeId = (item: QuoteType | Ref<QuoteType>) => {
  const __quote = isRef(item) ? item?.value : item;

  // TODO: we can remove this when we resolve (#2095)
  // (https://github.com/SummitESP/spyglass/issues/2095)
  // It allows for structuredClone to work and not bug out:
  __quote.quote_line_items = isProxy(__quote.quote_line_items)
    ? getArrayFromDeepNestedProxy(__quote.quote_line_items)
    : __quote.quote_line_items ;

  let _quote = structuredClone(toRaw(__quote));

  delete _quote.id;
  delete _quote.created_at;
  delete _quote.updated_at;
  delete (_quote as any).__typename;
  return _quote;
};

const removeIdFromQli = (qli: QuoteLineItemType | Ref<QuoteLineItemType>) => {
  const __qli = isRef(qli) ? qli?.value : qli;
  let _qli = structuredClone(toRaw(__qli));
  if (_qli?.line_item_type) {
    _qli = serializeQuoteLineItem(_qli);
    if (_qli.source !== Quote_Line_Item_Source_Dataset_Enum.ChangeOrder) {
      _qli.source = Quote_Line_Item_Source_Dataset_Enum.CopyQuote;
    }
  }

  delete _qli.id;
  delete _qli.quote_id;
  delete _qli.created_at;
  delete _qli.updated_at;
  delete (_qli as any).__typename; // TODO: optional typename?
  return _qli;
};

const copyQuote = () => {
  const _quote: any = removeId(quote as QuoteType | Ref<QuoteType>); // <-- TODO: remove as and resolve
  _quote.version_number = 1;
  _quote.source = Quote_Source_Dataset_Enum.CopyQuote;
  _quote.status = Quote_Status_Dataset_Enum.Draft;
  delete _quote.sk_job_type;
  delete _quote.quote_scenario;
  delete _quote.deleted_at;
  delete _quote.parent;
  delete _quote.user;
  delete _quote.name;

  quoteHeaderFields.forEach(field => {
    delete _quote[field];
  });

  const _quote_line_items = (quote?.value?.quote_line_items || []).map(
    removeIdFromQli
  ).filter((qli) => {
    return !isServiceLineItemAddedFromJobType(qli);
  }).map((qli, index) => {
    // Edge case: Recaculate all suborders if users re-ordered their service
    // line items before hitting 'Copy Quote Line Items' (they get filtered out).
    return {
      ...qli,
      sub_order: index + 1
    };
  });

  _quote.quote_line_items = { data: _quote_line_items };

  return getQuotesCount()
    .then((count_resp) => {
      // TODO: possible edge case with aggregate count not coming back
      const proposal_count =
        1000001 + (count_resp?.data?.value?.quotes_aggregate?.aggregate?.count as number);
      const skProposalNumber = `SG${proposal_count}`;
      _quote.sk_proposal_number = skProposalNumber;
      return insertQuote({ object: _quote })
        .catch((err) => {
          console.error(err);
          alert("Error creating quote! Contact support.");
        });
    })
    .then(resp => {
      const _quoteId = resp?.data?.insert_quotes_one?.id;
      if (_quoteId) {
        return router.push({
          name: "EditQuote",
          params: {
            quoteId: _quoteId
          }
        });
      }
      captureException(
        new Error("Error Creating Quote From Copy Quote"),
        {
          extra: {
            quote_id: quote?.value?.id,
            response: resp?.data
          }
        }
      );
      throw new Error("Error creating quote!");
    })
};
</script>

<style scoped lang="scss"></style>
